/**
 * 클라이언트 암호화 유틸리티
 * E2E(종단간) 암호화를 위한 키 생성, 암호화, 복호화 기능
 * Web Crypto API 기반
 */

/** 암호화 알고리즘 상수 */
const ALGORITHM = 'AES-GCM';
const KEY_LENGTH = 256;
const IV_LENGTH = 12;

/**
 * ECDH 키 쌍 생성 (P-256)
 * 클라이언트 등록 시 사용
 */
export async function generateKeyPair(): Promise<{
  publicKey: string;
  privateKey: string;
}> {
  const keyPair = await crypto.subtle.generateKey(
    { name: 'ECDH', namedCurve: 'P-256' },
    true,
    ['deriveBits']
  );

  const publicKey = await crypto.subtle.exportKey('raw', keyPair.publicKey);
  const privateKey = await crypto.subtle.exportKey('pkcs8', keyPair.privateKey);

  return {
    publicKey: arrayBufferToBase64(publicKey),
    privateKey: arrayBufferToBase64(privateKey),
  };
}

/**
 * AES-GCM 암호화 키 생성
 * 각 채팅방마다 새로운 키를 생성하여 사용
 */
export async function generateAESKey(): Promise<string> {
  const key = await crypto.subtle.generateKey(
    { name: ALGORITHM, length: KEY_LENGTH },
    true,
    ['encrypt', 'decrypt']
  );
  const exported = await crypto.subtle.exportKey('raw', key);
  return arrayBufferToBase64(exported);
}

/**
 * AES 키를 CryptoKey 객체로 가져오기
 */
async function importAESKey(base64Key: string): Promise<CryptoKey> {
  const keyData = base64ToArrayBuffer(base64Key);
  return crypto.subtle.importKey(
    'raw',
    keyData,
    { name: ALGORITHM, length: KEY_LENGTH },
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * 메시지 암호화 (AES-256-GCM)
 * @param plaintext 평문 메시지
 * @param base64Key Base64 인코딩된 AES 키
 * @returns { encryptedContent: Base64 암호문, iv: Base64 IV }
 */
export async function encryptMessage(
  plaintext: string,
  base64Key: string
): Promise<{ encryptedContent: string; iv: string }> {
  const key = await importAESKey(base64Key);
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
  const encoded = new TextEncoder().encode(plaintext);

  const encrypted = await crypto.subtle.encrypt(
    { name: ALGORITHM, iv },
    key,
    encoded
  );

  return {
    encryptedContent: arrayBufferToBase64(encrypted),
    iv: arrayBufferToBase64(iv),
  };
}

/**
 * 메시지 복호화 (AES-256-GCM)
 * @param encryptedContent Base64 암호문
 * @param base64IV Base64 IV
 * @param base64Key Base64 AES 키
 * @returns 복호화된 평문
 */
export async function decryptMessage(
  encryptedContent: string,
  base64IV: string,
  base64Key: string
): Promise<string> {
  const key = await importAESKey(base64Key);
  const iv = base64ToArrayBuffer(base64IV);
  const data = base64ToArrayBuffer(encryptedContent);

  const decrypted = await crypto.subtle.decrypt(
    { name: ALGORITHM, iv },
    key,
    data
  );

  return new TextDecoder().decode(decrypted);
}

/**
 * 채팅방 키를 비밀번호로 파생 (키 교환 용도)
 * 비밀번호 기반 키 교환을 위한 임시 구현
 */
export async function deriveKeyFromPassword(
  password: string,
  salt: string
): Promise<string> {
  const encoder = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    encoder.encode(password),
    'PBKDF2',
    false,
    ['deriveBits']
  );

  const derived = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt: encoder.encode(salt),
      iterations: 150000,
      hash: 'SHA-256',
    },
    keyMaterial,
    256
  );

  return arrayBufferToBase64(derived);
}

// ---- 내부 유틸리티 ----

function arrayBufferToBase64(buffer: ArrayBuffer | Uint8Array): string {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function base64ToArrayBuffer(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}
